import React, { useState } from 'react';
import { Table, Tag, Button, Modal, Form, Input, Select, InputNumber, message, Space, Row, Col, Popconfirm, DatePicker } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, DollarOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

const Courses = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    // Filter states
    const [filterInstructor, setFilterInstructor] = useState(null);
    const [filterStatus, setFilterStatus] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isExpenseModalVisible, setIsExpenseModalVisible] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [editingCourse, setEditingCourse] = useState(null);
    const [form] = Form.useForm();
    const [expenseForm] = Form.useForm();
    const queryClient = useQueryClient();

    const { data: coursesData, isLoading } = useQuery('courses', async () => {
        const response = await api.get('/courses');
        return response.data.data;
    }, {
        refetchOnMount: 'always',
        refetchOnWindowFocus: false
    });

    const { data: staffData } = useQuery('staff-teachers', async () => {
        const response = await api.get('/staff?position=teacher');
        return response.data.data;
    }, {
        refetchOnMount: 'always',
        refetchOnWindowFocus: false
    });

    const { data: subjectsData } = useQuery('subjects', async () => {
        const response = await api.get('/subjects');
        return response.data.data;
    });

    const { data: expenseTypesData } = useQuery('expense-types', async () => {
        const response = await api.get('/expense-types');
        return response.data.data;
    });

    const createExpenseMutation = useMutation(
        (data) => api.post('/expenses', { ...data, relatedTo: { model: 'Course', id: selectedCourse._id } }),
        {
            onSuccess: () => {
                message.success(t('expenseAddedSuccessfully'));
                setIsExpenseModalVisible(false);
                expenseForm.resetFields();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || 'Failed to add expense');
            },
        }
    );

    const createMutation = useMutation(
        (data) => api.post('/courses', data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('courses');
                queryClient.refetchQueries('courses');
                queryClient.refetchQueries('staff-teachers');
                queryClient.refetchQueries('subjects');
                message.success(t('createdSuccessfully'));
                setIsModalVisible(false);
                form.resetFields();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || 'Failed to create course');
            },
        }
    );

    const updateMutation = useMutation(
        ({ id, data }) => api.put(`/courses/${id}`, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('courses');
                queryClient.refetchQueries('courses');
                queryClient.refetchQueries('staff-teachers');
                queryClient.refetchQueries('subjects');
                message.success(t('updatedSuccessfully'));
                setIsModalVisible(false);
                setEditingCourse(null);
                form.resetFields();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || 'Failed to update course');
            },
        }
    );

    const deleteMutation = useMutation(
        (id) => api.delete(`/courses/${id}`),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('courses');
                message.success(t('deletedSuccessfully'));
            },
            onError: (error) => {
                message.error(error.response?.data?.message || 'Failed to delete course');
            },
        }
    );

    const completeMutation = useMutation(
        (id) => api.post(`/courses/${id}/complete`),
        {
            onSuccess: (response) => {
                queryClient.invalidateQueries('courses');
                queryClient.invalidateQueries('certificates');
                message.success(response.data.message || t('courseCompletedSuccessfully'));
            },
            onError: (error) => {
                const errorMessage = error.response?.data?.message || t('failedToCompleteCourse');
                message.error(errorMessage);
            },
        }
    );

    const handleSubmit = (values) => {
        const formattedValues = {
            ...values,
            duration: {
                value: values.durationValue,
                unit: values.durationUnit,
            },
            fee: {
                amount: values.feeAmount,
                currency: values.currency || 'USD',
            }, discount: values.discount || 0, capacity: {
                min: values.minCapacity || 5,
                max: values.maxCapacity || 20,
            },
        };

        // Remove flat fields
        delete formattedValues.durationValue;
        delete formattedValues.durationUnit;
        delete formattedValues.feeAmount;
        delete formattedValues.currency;
        delete formattedValues.minCapacity;
        delete formattedValues.maxCapacity;

        if (editingCourse) {
            updateMutation.mutate({ id: editingCourse._id, data: formattedValues });
        } else {
            createMutation.mutate(formattedValues);
        }
    };

    const handleEdit = (record) => {
        setEditingCourse(record);
        form.setFieldsValue({
            ...record,
            subject: record.subject?._id,
            durationValue: record.duration?.value,
            durationUnit: record.duration?.unit,
            feeAmount: record.fee?.amount,
            discount: record.discount || 0,
            currency: record.fee?.currency,
            minCapacity: record.capacity?.min,
            maxCapacity: record.capacity?.max,
        });
        setIsModalVisible(true);
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: t('confirmDelete'),
            content: t('actionCannotBeUndone'),
            onOk: () => deleteMutation.mutate(id),
        });
    };

    const handleComplete = (id) => {
        completeMutation.mutate(id);
    };

    const columns = [
        { title: t('code'), dataIndex: 'code', key: 'code', width: 100 },
        {
            title: t('courseName'),
            dataIndex: 'name',
            key: 'name',
            render: (name, record) => (
                <a
                    onClick={() => navigate(`/courses/${record._id}`)}
                    style={{ cursor: 'pointer', color: '#1890ff' }}
                >
                    {name}
                </a>
            )
        },
        {
            title: t('subject'),
            key: 'subject',
            render: (_, record) => {
                if (!record.subject) return '-';
                if (typeof record.subject === 'string') return record.subject;
                if (typeof record.subject.name === 'object') {
                    return record.subject.name[i18n.language] || record.subject.name.en || record.subject.name.ku || record.subject.name.ar || '-';
                }
                return record.subject.name || '-';
            }
        },
        {
            title: t('duration'),
            key: 'duration',
            render: (_, r) => `${r.duration?.value} ${r.duration?.unit}`,
        },
        {
            title: t('fee'), key: 'fee', render: (_, r) => {
                const currency = r.fee?.currency || 'USD';
                const symbol = currency === 'IQD' ? 'IQD' : '$';
                const amount = r.fee?.amount || 0;
                const discount = r.discount || 0;
                const finalAmount = amount - discount;

                if (discount > 0) {
                    return (
                        <span>
                            <span style={{ textDecoration: 'line-through', color: '#999', marginRight: '8px' }}>
                                {symbol}{amount.toLocaleString()}
                            </span>
                            <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                                {symbol}{finalAmount.toLocaleString()}
                            </span>
                            <Tag color="green" style={{ marginLeft: '8px' }}>-{symbol}{discount.toLocaleString()}</Tag>
                        </span>
                    );
                }
                return `${symbol}${amount.toLocaleString()}`;
            }
        },
        {
            title: t('status'),
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive) => <Tag color={isActive ? 'green' : 'red'}>{isActive ? t('active').toUpperCase() : t('inactive').toUpperCase()}</Tag>,
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
                    <Button
                        icon={<DollarOutlined />}
                        onClick={() => {
                            setSelectedCourse(record);
                            setIsExpenseModalVisible(true);
                        }}
                        size="small"
                        type="default"
                    >
                        {t('expenses')}
                    </Button>
                    {record.isActive && (
                        <Popconfirm
                            title={t('completeCourseConfirm')}
                            description={t('completeCourseDescription')}
                            onConfirm={() => handleComplete(record._id)}
                            okText={t('yes')}
                            cancelText={t('no')}
                        >
                            <Button
                                icon={<CheckCircleOutlined />}
                                type="primary"
                                size="small"
                            >
                                {t('complete')}
                            </Button>
                        </Popconfirm>
                    )}
                    <Button
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => handleDelete(record._id)}
                        size="small"
                    />
                </Space>
            ),
        },
    ];

    // Filtering logic
    let filteredCourses = coursesData || [];
    if (filterInstructor) {
        filteredCourses = filteredCourses.filter(course => course.instructor?._id === filterInstructor);
    }
    if (filterStatus !== null) {
        filteredCourses = filteredCourses.filter(course => {
            // status can be boolean or string
            if (typeof course.isActive === 'boolean') {
                return filterStatus === 'active' ? course.isActive : !course.isActive;
            }
            return course.status === filterStatus;
        });
    }
    if (searchText) {
        filteredCourses = filteredCourses.filter(course =>
            course.name?.toLowerCase().includes(searchText.toLowerCase()) ||
            course.code?.toLowerCase().includes(searchText.toLowerCase())
        );
    }

    return (
        <div>
            <div className="page-header">
                <h1>{t('courses')}</h1>
                <Space className="filter-section" wrap style={{ gap: '12px' }}>
                    <Select
                        placeholder={t('filterByInstructor')}
                        allowClear
                        style={{ width: 250 }}
                        value={filterInstructor}
                        onChange={setFilterInstructor}
                    >
                        {staffData?.map(inst => (
                            <Option key={inst._id} value={inst._id}>{inst.firstName} {inst.lastName}</Option>
                        ))}
                    </Select>
                    <Select
                        placeholder={t('filterByStatus')}
                        allowClear
                        style={{ width: 200 }}
                        value={filterStatus}
                        onChange={setFilterStatus}
                    >
                        <Option value="active">{t('active')}</Option>
                        <Option value="inactive">{t('inactive')}</Option>
                    </Select>
                    <Input.Search
                        placeholder={t('searchByNameOrCode')}
                        allowClear
                        style={{ width: 280 }}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditingCourse(null);
                            form.resetFields();
                            setIsModalVisible(true);
                        }}
                    >
                        {t('addCourse')}
                    </Button>
                </Space>
            </div>

            <Table
                columns={columns}
                dataSource={filteredCourses}
                rowKey="_id"
                loading={isLoading}
            />

            <Modal
                title={editingCourse ? t('editCourse') : t('addCourse')}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEditingCourse(null);
                    form.resetFields();
                }}
                footer={null}
                width={700}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="name"
                                label={t('courseName')}
                                rules={[{ required: true, message: 'Please enter course name' }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="code"
                                label={t('courseCode')}
                                rules={[{ required: true, message: 'Please enter course code' }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="subject"
                                label={t('subject')}
                                rules={[{ required: true, message: 'Please select subject' }]}
                            >
                                <Select
                                    placeholder={t('selectSubject')}
                                    showSearch
                                    filterOption={(input, option) =>
                                        option?.children?.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {subjectsData?.map(subject => (
                                        <Option key={subject._id} value={subject._id}>
                                            {subject.name?.[i18n.language] || subject.name?.en || subject.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="durationValue"
                                label={t('duration')}
                                rules={[{ required: true, message: 'Please enter duration' }]}
                            >
                                <InputNumber min={1} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="durationUnit"
                                label={t('unit')}
                                rules={[{ required: true, message: 'Please select unit' }]}
                            >
                                <Select>
                                    <Option value="days">{t('days')}</Option>
                                    <Option value="weeks">{t('weeks')}</Option>
                                    <Option value="months">{t('months')}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="feeAmount"
                                label={t('fee')}
                                rules={[{ required: true, message: 'Please enter fee' }]}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="discount"
                                label={t('discount')}
                                tooltip={t('discountOnCourseFee') || 'Discount amount on the course fee'}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="currency"
                                label={t('currency')}
                                rules={[{ required: true, message: 'Please select currency' }]}
                            >
                                <Select>
                                    <Option value="USD">{t('USD')}</Option>
                                    <Option value="IQD">{t('IQD')}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="minCapacity"
                                label={t('minCapacity')}
                            >
                                <InputNumber min={1} defaultValue={5} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="maxCapacity"
                                label={t('maxCapacity')}
                            >
                                <InputNumber min={1} defaultValue={20} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row> */}

                    <Form.Item
                        name="groups"
                        label={t('courseGroups')}
                        tooltip={t('courseGroupsTooltip')}
                    >
                        <Select mode="tags" placeholder={t('selectGroup')} />
                    </Form.Item>

                    <Form.Item
                        name="instructor"
                        label={t('instructor')}
                    >
                        <Select allowClear placeholder={t('selectInstructor')}>
                            {staffData?.map(staff => (
                                <Option key={staff._id} value={staff._id}>
                                    {staff.firstName} {staff.lastName}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label={t('description')}
                    >
                        <TextArea rows={3} />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                {editingCourse ? t('update') : t('create')}
                            </Button>
                            <Button onClick={() => {
                                setIsModalVisible(false);
                                setEditingCourse(null);
                                form.resetFields();
                            }}>
                                {t('cancel')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Course Expense Modal */}
            <Modal
                title={`${t('addCourseExpense')}: ${selectedCourse?.name || ''}`}
                open={isExpenseModalVisible}
                onCancel={() => {
                    setIsExpenseModalVisible(false);
                    setSelectedCourse(null);
                    expenseForm.resetFields();
                }}
                footer={null}
                width={600}
            >
                <Form
                    form={expenseForm}
                    layout="vertical"
                    onFinish={(values) => {
                        const formattedValues = {
                            ...values,
                            date: values.date?.toISOString(),
                            relatedTo: {
                                type: 'course',
                                reference: selectedCourse._id,
                                name: selectedCourse.name
                            }
                        };
                        createExpenseMutation.mutate(formattedValues);
                    }}
                >
                    <Form.Item
                        name="description"
                        label={t('description')}
                        rules={[{ required: true, message: t('pleaseEnterDescription') }]}
                    >
                        <Input placeholder={t('expenseDescriptionPlaceholder')} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="amount"
                                label={t('amount')}
                                rules={[{ required: true, message: t('pleaseEnterAmount') }]}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="currency"
                                label={t('currency')}
                                rules={[{ required: true, message: t('pleaseSelectCurrency') }]}
                                initialValue="USD"
                            >
                                <Select>
                                    <Option value="USD">{t('USD')}</Option>
                                    <Option value="IQD">{t('IQD')}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="category"
                        label={t('expenseType')}
                        rules={[{ required: true, message: t('pleaseSelectExpenseType') }]}
                    >
                        <Select placeholder={t('selectExpenseType')}>
                            {expenseTypesData?.map(type => (
                                <Option key={type._id} value={type.name}>
                                    {type.name}
                                </Option>
                            ))}
                            <Option value="social-media">{t('socialMedia')}</Option>
                            <Option value="advertising">{t('advertising')}</Option>
                            <Option value="materials">{t('materials')}</Option>
                            <Option value="other">{t('other')}</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="date"
                        label={t('date')}
                        initialValue={dayjs()}
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="affectsCommission"
                        label={t('affectsCommission') || 'Deduct from commission calculation'}
                        tooltip={t('affectsCommissionTooltip') || 'If enabled, this expense will be deducted from course income before calculating staff commission'}
                        initialValue={true}
                    >
                        <Select>
                            <Option value={true}>{t('yes') || 'Yes'}</Option>
                            <Option value={false}>{t('no') || 'No'}</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="notes"
                        label={t('notes')}
                    >
                        <TextArea rows={3} placeholder={t('additionalNotes')} />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={createExpenseMutation.isLoading}>
                                {t('add')}
                            </Button>
                            <Button onClick={() => {
                                setIsExpenseModalVisible(false);
                                setSelectedCourse(null);
                                expenseForm.resetFields();
                            }}>
                                {t('cancel')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Courses;
